import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { styled, useTheme } from '@mui/material/styles';
import { useAppSelector } from '../../../common/hooks';
import { InfoTooltip } from '../../molecules/InfoTooltip';
import ErrorIcon from '@mui/icons-material/Error';
import { ReactComponent as CalendarDateRangeIcon } from '../../../assets/icons/calendar-date-range.svg';

dayjs.extend(customParseFormat);
export interface DatePickerProps {
    type?: 'single' | 'range';
    onChange?: any;
    onBlur?: any;
    value?: Dayjs | null;
    labelStart?: string;
    labelEnd?: string;
    startDate?: string;
    endDate?: string;
    timeselection?: boolean;
    enabled?: boolean;
    hidden?: boolean;
    isMmYyyy?: boolean;
    style?: string;
    selectedSdate?: string;
    selectedEdate?: Dayjs;
    slotProps?: Object;
    minErrorMsg?: string;
    maxErrorMsg?: string;
    isYyyy?: boolean;
    customOpenIcon?: React.ReactNode;
    isMm?: boolean;
    hasError?: boolean;
    showErrorMessage?: boolean;
    popupPlacement?:
        | 'top'
        | 'bottom'
        | 'left'
        | 'right'
        | 'top-start'
        | 'top-end'
        | 'bottom-start'
        | 'bottom-end'
        | 'left-start'
        | 'left-end'
        | 'right-start'
        | 'right-end';
    popupOffset?: [number, number]; // [horizontal, vertical] offset in pixels
    placeHolderFormat?: string;
    onValidationError?: Function;
}

const StyledBasicDatePicker = styled(DatePicker)<{ hasError?: boolean }>(({ theme, hasError }) => ({
    '& .MuiIconButton-root': {
        padding: '4px',
        background: hasError ? theme.palette.semantic.bg.error : theme.palette.semantic.bg.brandLight2,
        '& svg:not(.error-svg)': {
            width: '16px',
            height: '16px',
            color: hasError ? theme.palette.error[500] : theme.palette.semantic.icon.brand
        },
        '& .error-svg': {
            display: 'flex'
        },
        '& svg.calendar-date-range-icon': {
            width: '20px',
            height: '20px',
            flexShrink: 0,
            '& path': { fill: theme.palette.semantic.icon.brand }
        },
        height: '100%',
        width: '2.4rem',
        /* position: relative; */
        position: 'absolute',
        right: '0px',
        borderRadius: '0px',
        borderTopRightRadius: '5px',
        borderBottomRightRadius: '5px'
    },
    '& .MuiDayCalendar-root': {
        '& .MuiPickersDay-root': {
            '& .MuiPickersDay-dayWithMargin': {
                '& .Mui-selected, :hover, :focus': {
                    backgroundColor: theme.palette.primary[500]
                }
            }
        }
    },
    '& .MuiMonthCalendar-root': {
        '& .MuiPickersMonth-root': {
            '& .MuiPickersMonth-monthButton': {
                '& .Mui-selected, :hover, :focus': {
                    backgroundColor: theme.palette.primary[500]
                }
            }
        }
    },
    '& .MuiYearCalendar-root': {
        '& .MuiPickersYear-root': {
            '& .MuiPickersYear-yearButton': {
                '& .Mui-selected, :hover, :focus': {
                    backgroundColor: theme.palette.primary[500]
                }
            }
        }
    }
}));

export const BasicDatePicker = ({
    type = 'single',
    labelStart,
    labelEnd,
    hidden,
    enabled,
    startDate,
    endDate,
    timeselection,
    isMmYyyy,
    isYyyy,
    isMm,
    onChange,
    onBlur,
    value,
    slotProps,
    minErrorMsg,
    maxErrorMsg,
    customOpenIcon = false,
    hasError = false,
    popupPlacement = 'bottom-end',
    popupOffset = [0, 8],
    placeHolderFormat,
    onValidationError
}: DatePickerProps) => {
    const theme = useTheme();
    const dateFormat = useAppSelector((state) => state.global)?.globalConfig?.dateFormat || 'MM/DD/YYYY';
    const [selectedStartDate] = useState<Dayjs | null>(dayjs(''));
    const errorMessage = minErrorMsg || maxErrorMsg;
    const PickerOpenIcon = () => {
        if (hasError) {
            return errorMessage ? (
                <InfoTooltip message={errorMessage} placement="bottom" bordered open>
                    <ErrorIcon className="error-svg" sx={{ color: theme.palette.error.main, cursor: 'pointer' }} />
                </InfoTooltip>
            ) : (
                <ErrorIcon />
            );
        }

        if (customOpenIcon) {
            return <CalendarDateRangeIcon className="calendar-date-range-icon" aria-hidden />;
        }

        return <CalendarMonthIcon />;
    };

    const components = {
        // Keep both keys for compatibility with different picker slot APIs.
        openPickerIcon: PickerOpenIcon,
        OpenPickerIcon: PickerOpenIcon
    };

    const handleChange = (payload: any) => {
        if (!onChange) return;

        onChange(payload);
    };

    const getPlaceholderOrFormat = () =>
        placeHolderFormat ? placeHolderFormat : isMmYyyy ? 'MM/YYYY' : isYyyy ? 'YYYY' : isMm ? 'MMM' : dateFormat;

    const updateValue = (newValue: Dayjs | null) => {
        if (isMmYyyy) {
            return dayjs(newValue).startOf('month');
        }
        return dayjs(newValue);
    };

    return (
        <>
            {!hidden &&
                (timeselection ? (
                    <DateTimePicker
                        label={labelStart}
                        views={
                            isMmYyyy
                                ? ['year', 'month', 'day', 'hours', 'minutes', 'seconds']
                                : isYyyy
                                ? ['year']
                                : isMm
                                ? ['month']
                                : ['year', 'month', 'day', 'hours', 'minutes', 'seconds']
                        }
                        disabled={!enabled}
                        minDateTime={dayjs(startDate)}
                        maxDateTime={dayjs(endDate)}
                        onChange={isMmYyyy ? undefined : handleChange}
                        onAccept={isMmYyyy ? handleChange : undefined}
                        value={dayjs(value)}
                        slotProps={slotProps}
                        open
                        slots={components}
                        format={getPlaceholderOrFormat()}
                    />
                ) : (
                    <StyledBasicDatePicker
                        key={`${isMmYyyy}-${isYyyy}-${isMm}`}
                        label={labelStart}
                        views={
                            isMmYyyy
                                ? ['year', 'month']
                                : isYyyy
                                ? ['year']
                                : isMm
                                ? ['month']
                                : ['year', 'month', 'day']
                        }
                        disabled={!enabled}
                        minDate={startDate ? dayjs(startDate) : undefined}
                        maxDate={endDate ? dayjs(endDate) : undefined}
                        onChange={isMmYyyy ? undefined : handleChange}
                        onAccept={isMmYyyy ? handleChange : undefined}
                        value={value === null || value === undefined ? null : updateValue(value)}
                        format={getPlaceholderOrFormat()}
                        onError={(newError: any, value: any) => {
                            onValidationError?.(newError, value);
                        }}
                        hasError={hasError}
                        slotProps={{
                            textField: {
                                onBlur,
                                onChange: handleChange,
                                sx: {
                                    '& .MuiInputBase-input': { fontWeight: 400 }
                                },
                                // InputProps: {
                                //     startAdornment: hasAnyError ? (
                                //         <InputAdornment position="start">
                                //             <InfoErrorIcon height={20} />
                                //         </InputAdornment>
                                //     ) : undefined
                                // },
                                FormHelperTextProps: {
                                    sx: {
                                        color: theme.palette.berry[400],
                                        fontStyle: 'italic',
                                        fontSize: '14px !important'
                                    }
                                },
                                placeholder: getPlaceholderOrFormat(),
                                error: hasError
                            },
                            calendarHeader: { sx: isMm ? { div: { marginRight: '-18px' } } : {} },
                            popper: {
                                placement: popupPlacement,
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: popupOffset
                                        }
                                    },
                                    {
                                        name: 'preventOverflow',
                                        options: {
                                            boundary: 'viewport'
                                        }
                                    }
                                ]
                            },
                            ...slotProps
                        }}
                        slots={components}
                        openTo={isMm ? 'month' : 'year'}
                    />
                ))}

            {!hidden &&
                type != 'single' &&
                (timeselection ? (
                    <DateTimePicker
                        label={labelEnd}
                        views={
                            isMmYyyy
                                ? ['year', 'month', 'hours', 'minutes', 'seconds']
                                : ['year', 'month', 'day', 'hours', 'minutes', 'seconds']
                        }
                        value={dayjs(endDate)}
                        disabled={!enabled}
                        slotProps={slotProps}
                        minDateTime={dayjs('')}
                        maxDateTime={dayjs(endDate)}
                        onChange={handleChange}
                        format={isMmYyyy ? 'MM/YYYY' : dateFormat}
                    />
                ) : (
                    <DatePicker
                        label={labelEnd}
                        views={isMmYyyy ? ['year', 'month'] : ['year', 'month', 'day']}
                        slotProps={slotProps}
                        disabled={!enabled}
                        minDate={dayjs(selectedStartDate)}
                        maxDate={dayjs(endDate)}
                        onChange={handleChange}
                        value={dayjs(value)}
                        format={isMmYyyy ? 'MM/YYYY' : dateFormat}
                    />
                ))}
        </>
    );
};
